'use client';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { MediaType } from '@repo/ui/src/types/image';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import { useRef, useEffect, useState } from 'react';
import { ToutContent } from '@repo/ui/src/components/ToutContent/_toutContent';
import { classNames } from '@repo/ui/src/utils/classNames';
import { appConfig } from '../../utils/appConfig';
import styles from './_GridCard.module.css';
import BackgroundVideo from '@repo/ui/src/components/BackgroundVideo';

interface CardProps {
  img: {
    image: MediaType;
    altTag: string;
  };
  toutContent: ToutContentProps;
  largeCard: boolean;
  reverse?: boolean;
  isMeaterMobile?: boolean;
  video?: MediaType;
}

const GridCard = ({ img, toutContent, largeCard, reverse, isMeaterMobile, video }: CardProps) => {
  const ref = useRef<any>(null);
  const [translateHeight, setHeight] = useState(0);
  const imageUrl = buildImageSrc(img.image, 700, 1, 1);

  useEffect(() => {
    const element = ref.current;
    if (element) {
      const bottomContent = element.querySelector('.bottom-content');
      const bottomContentHeight = bottomContent?.clientHeight;
      if (bottomContentHeight) {
        setHeight(bottomContentHeight);
      }
    }
  }, []);

  if (isMeaterMobile) {
    return (
      <li className="tw-relative tw-w-full tw-aspect-square tw-overflow-hidden" tabIndex={0}>
        {video ? (
          <BackgroundVideo
            video={video}
            width={700}
            fillContainer
            className="tw-absolute tw-inset-0 tw-h-full"
            playButtonPosition="right"
          />
        ) : (
          <img src={imageUrl} alt="" className="tw-w-full tw-h-full tw-object-cover" />
        )}
        <div className="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-gradient-to-t tw-from-[rgba(16,16,16,1)] tw-to-[rgba(0,0,0,0)]" />
        <div className="tw-absolute tw-bottom-0 tw-left-0 tw-w-full tw-p-5">
          <ToutContent
            dark
            headingStyleVariant="h5"
            heading={toutContent.heading}
            {...toutContent}
            largeCard={largeCard}
            align="left"
            divider
            buttonSize="small"
          />
        </div>
      </li>
    );
  }

  return (
    <li
      className={classNames(
        'tw-group/card accCard',
        styles.gridCard,
        reverse ? styles.reverse : styles.normal,
      )}
      ref={ref}
      tabIndex={0}
      style={{
        ['--translate-height' as any]: `${translateHeight + 4}px`,
      }}
    >
      {video ? (
        <div className="tw-row-start-1 tw-col-start-1 tw-w-full tw-aspect-square">
          <BackgroundVideo
            video={video}
            width={700}
            fillContainer
            className="tw-h-full"
            playButtonPosition="right"
          />
        </div>
      ) : (
        <img
          src={imageUrl}
          alt=""
          className="tw-w-full tw-h-auto tw-object-cover tw-row-start-1 tw-col-start-1 tw-aspect-square"
        />
      )}
      <div className="tw-absolute tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-gradient-to-t tw-from-[rgba(16,16,16,1)] tw-to-[rgba(0,0,0,0)]" />
      <div
        className="tout tw-flex tw-px-5 tw-flex-col tw-justify-end tw-row-start-1 tw-col-start-1 tw-w-full tw-h-full tw-transition-transform tw-duration-[600ms]
          [&_.bottom-content]:tw-absolute [&_.bottom-content]:tw-pr-5 [&_.bottom-content]:tw-transition-all [&_.bottom-content]:tw-duration-[800ms] [&_.bottom-content]:tw-ease-out
          group-hover/card:[&_.bottom-content]:tw-opacity-100 focus:[&_.bottom-content]:tw-opacity-100"
      >
        <ToutContent
          dark
          headingStyleVariant={appConfig.isTraeger ? 'h5' : 'h3'}
          heading={toutContent.heading}
          {...toutContent}
          largeCard={largeCard}
          align="left"
          divider
          buttonSize="small"
          className={appConfig.isMeater && '[&_h2]:tw-uppercase'}
        />
      </div>
    </li>
  );
};

export default GridCard;
